import { XIcon, ArrowLeft } from "lucide-react";
import { useLayoutEffect, useRef } from "react";
import { Drawer } from "vaul";

import { useCKEditorDrawerCompat } from "./useCKEditorDrawerCompat";

export type BaseDrawerProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title?: React.ReactNode
  description?: React.ReactNode
  children?: React.ReactNode
  footer?: React.ReactNode
  headerIndicator?: React.ReactNode
  isFirstLevel?: boolean
  onBack?: () => void
  width?: string
  zIndex?: number
  bodyClassName?: string
  drawerId?: string
  ckEditorCompatible?: boolean
};

type DrawerContentProps = React.ComponentProps<typeof Drawer.Content>;
type OutsideInteractionEvent = Parameters<NonNullable<DrawerContentProps["onInteractOutside"]>>[0];

const DrawerCloseButton = ({ onOpenChange }: { onOpenChange: (open: boolean) => void }) => (
  <button
    type="button"
    onClick={() => onOpenChange(false)}
    className="cursor-pointer bg-gray-50 hover:bg-gray-100 ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground rounded-md opacity-70 transition-all hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-5 p-1.5"
  >
    <XIcon />
    <span className="sr-only">Close</span>
  </button>
);

const BaseDrawer = ({
  open, onOpenChange, title, description, children, footer, headerIndicator, isFirstLevel = true, onBack, width = "550px", zIndex, bodyClassName, drawerId, ckEditorCompatible,
}: BaseDrawerProps) => {
  const contentElementRef = useRef<HTMLDivElement | null>(null);
  const overlayElementRef = useRef<HTMLDivElement | null>(null);
  const { ckEditorContentProps } = useCKEditorDrawerCompat(open, ckEditorCompatible);

  // Radix considère « dehors » tout ce qui est rendu hors de l'arbre React du
  // drawer, même si c'est affiché PAR-DESSUS lui : c'est le cas des toasts
  // sonner (portalés à la racine et forcés en `pointer-events: auto` pour rester
  // cliquables au-dessus des modales). Un clic sur un bouton du drawer recouvert
  // par un toast atterrissait donc sur le toast et fermait toute la pile.
  // On ne ferme plus que sur l'overlay du drawer lui-même.
  const preventDismissOutsideOverlay = (event: OutsideInteractionEvent) => {
    const target = event.detail.originalEvent.target;
    if (!(target instanceof Node) || !overlayElementRef.current?.contains(target)) {
      event.preventDefault();
    }
  };

  useLayoutEffect(() => {
    if (!open) return;
    const activeElement = document.activeElement as HTMLElement | null;
    if (activeElement && typeof activeElement.blur === "function") {
      activeElement.blur();
    }
    if (contentElementRef.current) {
      contentElementRef.current.focus();
    }
  }, [open]);

  return (
    <Drawer.Root
      direction="right"
      open={open}
      // Tout dismiss (overlay, escape, drag) passe par onOpenChange, y compris
      // pour un sous-drawer (isFirstLevel=false) : un clic sur l'overlay ferme
      // toute la pile en un geste. Le bouton "retour" reste seul câblé sur onBack
      // pour ne remonter que d'un niveau.
      onOpenChange={onOpenChange}
      handleOnly
    >
      <Drawer.Portal>
        <Drawer.Overlay ref={overlayElementRef} className="fixed inset-0 bg-black/40" style={{ zIndex: zIndex ? zIndex - 10 : 30 }} />
        <Drawer.Content
          ref={contentElementRef}
          role="dialog"
          aria-modal="true"
          tabIndex={-1}
          className="right-0 top-0 bottom-0 fixed outline-none w-[550px] bg-zinc-50 flex flew-row justify-center items-center"
          style={{ width, zIndex: zIndex || 40, userSelect: "text" }}
          {...(drawerId ? { "data-drawer-id": drawerId } : {})}
          onPointerDownOutside={(event) => {
            ckEditorContentProps.onPointerDownOutside?.(event);
            preventDismissOutsideOverlay(event);
          }}
          onInteractOutside={(event) => {
            ckEditorContentProps.onInteractOutside?.(event);
            preventDismissOutsideOverlay(event);
          }}
        >
          <Drawer.Handle
            dir="vertical"
            className="rotate-90 !w-8 cursor-grab z-20 overflow-hidden"
          />

          <div data-vaul-no-drag className={`bg-zinc-50 h-full w-full grow p-6 -ml-8 pl-9 flex flex-col overflow-y-auto ${bodyClassName ?? ""}`.trim()}>
            {drawerId && <div className="text-right text-[9px] text-slate-300 select-none -mb-1">{drawerId}</div>}
            <div>
              {/* Title and description */}
              <div className="flex items-start justify-between">
                {!isFirstLevel && (
                  <button
                    type="button"
                    onClick={onBack}
                    className="group cursor-pointer bg-gray-50 hover:bg-gray-100 ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground rounded-md opacity-70 transition-all hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-5 p-1.5 -ml-8"
                  >
                    <ArrowLeft className="pr-1 group-hover:animate-horizontal-bounce-left" />
                    <span className="sr-only">Retour</span>
                  </button>
                )}

                <div className="flex-1">
                  {title && <Drawer.Title className="text-2xl font-semibold text-gray-800">{title}</Drawer.Title>}
                  {description && (
                    <Drawer.Description className="text-xs leading-4 mt-1 text-gray-400">{description}</Drawer.Description>
                  )}
                </div>

                <DrawerCloseButton onOpenChange={onOpenChange} />
              </div>
            </div>
            {headerIndicator && <div className="[&:has(>*)]:mt-3">{headerIndicator}</div>}
            <div className="mt-4 min-h-0 flex-1 flex flex-col">{children}</div>
            {footer && <div className="mt-4">{footer}</div>}
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
};

export default BaseDrawer;
